import { useState } from "react"
import Chip from "Components/atoms/chip"
import { LambdaRunBatchView, LambdaTrackerUI } from "models/lambda-tracker"
import { formatDateTimeAmPm } from "utils/tools"

interface LambdaRunsPanelProps {
    batches: LambdaRunBatchView[]
    lambdasEnabled: boolean
    suffix: string
    onCancel: (batchId: string) => void
}

const ACTIVE = ["Queued", "Running"]

const duration = (start: string | null, end: string | null): string => {
    if (!start) return "-"
    const ms = (end ? new Date(end).getTime() : Date.now()) - new Date(start).getTime()
    return LambdaTrackerUI.duration(ms)
}

// Manual runs (Run / Update all): newest first; the active one is expanded with live progress.
const LambdaRunsPanel = ({ batches, lambdasEnabled, suffix, onCancel }: LambdaRunsPanelProps) => {
    const [expanded, setExpanded] = useState<string | null>(null)

    if (!lambdasEnabled) {
        return (
            <div className="alert alert-secondary mt-3">
                <i className="mdi mdi-information-outline me-1" aria-hidden />
                No Lambda writes to this environment's database, so manual runs are off here. Use QA or production.
            </div>
        )
    }

    if (batches.length === 0) {
        return <div className="text-center text-muted py-5">No manual runs yet. Use <strong>Run</strong> on a Lambda or <strong>Update all</strong>.</div>
    }

    return (
        <div className="lambda-runs">
            <small className="text-muted">Runs the <code>{suffix}</code> functions. Each run waits for the Lambda to finish; the result comes from the SyncLog it writes.</small>
            {batches.map(batch => {
                const total = batch.requests.length
                const done = total - batch.counts.Queued - batch.counts.Running
                const isActive = ACTIVE.includes(batch.status)
                const open = expanded === batch.id || (expanded === null && isActive)
                return (
                    <section key={batch.id} className={`lambda-run-batch ${isActive ? "active" : ""}`}>
                        <div className="batch-head">
                            <button type="button" className="btn btn-link p-0 text-start" onClick={() => setExpanded(open ? "" : batch.id)} aria-expanded={open}>
                                <i className={`mdi ${open ? "mdi-chevron-down" : "mdi-chevron-right"} me-1`} aria-hidden />
                                <strong>{batch.kind === "all" ? "Update all" : batch.requests[0]?.displayName ?? "Run"}</strong>
                                {batch.syncFrom && <span className="text-muted"> · since {batch.syncFrom}</span>}
                                {batch.fiscalYears.length > 0 && <span className="text-muted"> · {batch.fiscalYears.map(y => `FY${y}`).join(", ")}</span>}
                            </button>
                            <div className="d-flex align-items-center gap-2">
                                <Chip variant={LambdaTrackerUI.runStatusVariant(batch.status)}>
                                    <i className={`${LambdaTrackerUI.runStatusIcon(batch.status)} me-1`} aria-hidden />{batch.status}
                                </Chip>
                                {isActive && (
                                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => onCancel(batch.id)}>Cancel pending</button>
                                )}
                            </div>
                        </div>
                        <div className="batch-meta">
                            <small>{formatDateTimeAmPm(batch.createdAt)} · {batch.requestedByEmail} · {done}/{total} done · {duration(batch.startedAt, batch.finishedAt)}</small>
                            <small>
                                {batch.counts.Succeeded > 0 && <span className="text-success me-2">{batch.counts.Succeeded} ok</span>}
                                {batch.counts.Warning > 0 && <span className="text-warning me-2">{batch.counts.Warning} warning</span>}
                                {batch.counts.Failed > 0 && <span className="text-danger me-2">{batch.counts.Failed} failed</span>}
                                {batch.counts.Cancelled > 0 && <span className="text-muted">{batch.counts.Cancelled} cancelled</span>}
                            </small>
                        </div>
                        <div className="progress" role="progressbar" aria-valuenow={done} aria-valuemin={0} aria-valuemax={total} aria-label="Progress">
                            <div className="progress-bar bg-success" style={{ width: `${(batch.counts.Succeeded / total) * 100}%` }} />
                            <div className="progress-bar bg-warning" style={{ width: `${(batch.counts.Warning / total) * 100}%` }} />
                            <div className="progress-bar bg-danger" style={{ width: `${(batch.counts.Failed / total) * 100}%` }} />
                            <div className="progress-bar progress-bar-striped progress-bar-animated bg-info" style={{ width: `${(batch.counts.Running / total) * 100}%` }} />
                        </div>
                        {open && (
                            <div className="table-responsive mt-2">
                                <table className="table table-sm align-middle mb-0">
                                    <thead>
                                        <tr><th>Phase</th><th>Lambda</th><th>Range</th><th>Status</th><th>Duration</th><th>Detail</th></tr>
                                    </thead>
                                    <tbody>
                                        {batch.requests.map(r => (
                                            <tr key={r.id}>
                                                <td>{r.phase === 1 ? "1 · Sources" : "2 · Reports"}</td>
                                                <td>{r.displayName}<small className="d-block text-muted">{r.functionName}</small></td>
                                                <td className="text-nowrap">{r.label}</td>
                                                <td>
                                                    <Chip variant={LambdaTrackerUI.runStatusVariant(r.status)}>
                                                        <i className={`${LambdaTrackerUI.runStatusIcon(r.status)} me-1`} aria-hidden />{r.status}
                                                    </Chip>
                                                </td>
                                                <td className="text-nowrap">{duration(r.startedAt, r.finishedAt)}</td>
                                                <td className="run-error" title={r.errorMessage ?? ""}>
                                                    {r.errorMessage ?? ""}
                                                    {r.awsRequestId && <small className="d-block text-muted">Request {r.awsRequestId}</small>}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>
                )
            })}
        </div>
    )
}

export default LambdaRunsPanel
